import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { DateTime } from 'luxon';
import { DateFormat, TagColor } from '@equip.health/ui';
import { TreatmentEpisodeStatus } from '~/lib/constants/patients.constants';
import { getDiffInWeeks } from '~/lib/util/patient.util';

const inquiryResponseToChannelAndChannelTypeName = (
  inquiry: InquiryDetailResponse,
) =>
  `${inquiry.channel?.name ? inquiry.channel.name : ''}${inquiry.channelType?.name ? `, ${inquiry.channelType.name}` : ''}`;
const formatNewStatus = (
  inquiry: InquiryDetailResponse,
): PatientStatusCardFormatLines => ({
  lineOne: inquiryResponseToChannelAndChannelTypeName(inquiry),
  lineTwo:
    'Created ' +
    DateTime.fromISO(inquiry.createdAt).toFormat(DateFormat.monthDateYear),
});

const formatDefaultStatus = (
  inquiry: InquiryDetailResponse,
  lastContactDate?: string,
): PatientStatusCardFormatLines => ({
  lineOne: inquiryResponseToChannelAndChannelTypeName(inquiry),
  lineTwo: lastContactDate
    ? `Last contact ${DateTime.fromISO(lastContactDate).toFormat(DateFormat.monthDateYear)}`
    : `Inquiry created ${DateTime.fromISO(inquiry.inquiryDate).toFormat(DateFormat.monthDateYear)}`,
});

const formatEnrolledStatus = (
  inquiry: InquiryDetailResponse,
): PatientStatusCardFormatLines => ({
  lineOne: inquiryResponseToChannelAndChannelTypeName(inquiry),
  lineTwo: `Admit date ${
    inquiry.targetAdmissionDate
      ? DateTime.fromISO(inquiry.targetAdmissionDate).toFormat(
          DateFormat.monthDateYear,
        )
      : 'Not set'
  }`,
});

const formatClosedStatus = (
  inquiry: InquiryDetailResponse,
): PatientStatusCardFormatLines => ({
  lineOne: inquiry.closedReason?.description ?? 'No reason provided',
  lineTwo: `Closed ${DateTime.fromISO(inquiry.closedAt ?? inquiry.updatedAt).toFormat(DateFormat.monthDateYear)}`,
});

const inquiryStatusMapper: Record<
  InquiryStatusValue,
  (
    inquiry: InquiryDetailResponse,
    lastContactDate: string,
  ) => PatientStatusCardFormatLines
> = {
  [InquiryStatusValue.ASSESSING]: formatDefaultStatus,
  [InquiryStatusValue.FIELDED]: formatDefaultStatus,
  [InquiryStatusValue.NEW]: formatNewStatus,
  [InquiryStatusValue.QUALIFIED]: formatDefaultStatus,
  [InquiryStatusValue.ONBOARDING]: formatDefaultStatus,
  [InquiryStatusValue.ENROLLED]: formatEnrolledStatus,
  [InquiryStatusValue.CLOSED]: formatClosedStatus,
  [InquiryStatusValue.UNKNOWN]: () => ({
    lineOne: 'Unknown status',
    lineTwo: 'Unknown status',
  }),
};
export const formatInquiryText = (
  patientStatus: InquiryStatusValue,
  inquiry: InquiryDetailResponse,
  lastContactDate: string,
): PatientStatusCardFormatLines => {
  const formatter =
    inquiryStatusMapper[patientStatus] ??
    inquiryStatusMapper[InquiryStatusValue.UNKNOWN];
  return formatter(inquiry, lastContactDate);
};

const newStatusStyles: PatientStatusStyles = {
  backgroundColor: 'tertiary.marigold.background',
  color: 'tertiary.marigold.text',
  tagColor: TagColor.marigold,
};
const assessingStatusStyles: PatientStatusStyles = {
  backgroundColor: 'tertiary.orange.background',
  color: 'tertiary.orange.text',
  tagColor: TagColor.orange,
};
const qualifiedStatusStyles: PatientStatusStyles = {
  backgroundColor: 'violet.background',
  color: 'violet.text',
  tagColor: TagColor.purple,
};
const onboaringStatusStyles: PatientStatusStyles = {
  backgroundColor: 'plum.background',
  color: 'plum.text',
  tagColor: TagColor.pink,
};
const fieldedStatusStyles: PatientStatusStyles = {
  backgroundColor: 'secondary.10',
  color: 'secondary.70',
  tagColor: TagColor.periwinkle,
};
const activeStatusStyles: PatientStatusStyles = {
  backgroundColor: 'primary.10',
  color: 'primary.100',
  tagColor: TagColor.green,
};
const inactiveStatusStyles: PatientStatusStyles = {
  backgroundColor: 'neutral.background.secondary',
  color: 'neutral.secondary',
  tagColor: TagColor.gray,
};

export const inquiryStatusStylesMapper: Record<
  InquiryStatusValue,
  PatientStatusStyles
> = {
  [InquiryStatusValue.ASSESSING]: assessingStatusStyles,
  [InquiryStatusValue.CLOSED]: inactiveStatusStyles,
  [InquiryStatusValue.ENROLLED]: activeStatusStyles,
  [InquiryStatusValue.FIELDED]: fieldedStatusStyles,
  [InquiryStatusValue.NEW]: newStatusStyles,
  [InquiryStatusValue.ONBOARDING]: onboaringStatusStyles,
  [InquiryStatusValue.QUALIFIED]: qualifiedStatusStyles,
  [InquiryStatusValue.UNKNOWN]: inactiveStatusStyles,
};

const fallbackPrimaryDiagnosisLabel = 'No primary diagnosis';

const formatInTreatment = (
  episode: TreatmentEpisode,
  primaryDiagnosis: string,
  tentativeDischargeDate?: string,
): PatientStatusCardFormatLines => {
  const primaryDiagnosisText =
    primaryDiagnosis || fallbackPrimaryDiagnosisLabel;

  let lineTwo = '';
  if (episode.admitDate) {
    const numWeeks = getDiffInWeeks(
      episode.admitDate,
      episode.closedDate ?? new Date().toISOString().slice(0, 10),
      DateFormat.shortYearMonthDay,
    );
    const admitDateSlashFormat = DateTime.fromFormat(
      episode.admitDate,
      DateFormat.shortYearMonthDay,
    ).toFormat(DateFormat.mmDdYySlashes);

    if (tentativeDischargeDate && !episode.closedDate) {
      const dischargeDateSlashFormat = DateTime.fromFormat(
        tentativeDischargeDate,
        DateFormat.shortYearMonthDay,
      ).toFormat(DateFormat.mmDdYySlashes);
      lineTwo = `Week ${numWeeks > 0 ? numWeeks : 1} (${admitDateSlashFormat} \u2192 ${dischargeDateSlashFormat})`;
    } else {
      lineTwo = `Week ${numWeeks > 0 ? numWeeks : 1} (Admitted ${admitDateSlashFormat})`;
    }
  }
  return {
    lineOne: primaryDiagnosisText,
    lineTwo,
  };
};

const getPausedDate = (
  pausedDetail: EpisodePausedDetails,
  dateKey: keyof Pick<EpisodePausedDetails, 'startDate' | 'endDate'>,
): DateTime =>
  DateTime.fromFormat(pausedDetail[dateKey], DateFormat.shortYearMonthDay);

export const getActivePauseStartDate = (
  pausedDetails: EpisodePausedDetails[],
): string => {
  const activePausedDetail = (pausedDetails ?? []).find((detail) => {
    const startDate = getPausedDate(detail, 'startDate');
    const endDate = detail.endDate ? getPausedDate(detail, 'endDate') : null;
    const now = DateTime.fromFormat(
      DateTime.now().toISODate(),
      DateFormat.shortYearMonthDay,
    );
    return startDate <= now && (!endDate || endDate >= now);
  });
  if (!activePausedDetail?.startDate) {
    return undefined;
  }
  const activePauseStartDateTime = getPausedDate(
    activePausedDetail,
    'startDate',
  );
  return activePauseStartDateTime.toFormat(DateFormat.mmDdYySlashes);
};

const formatPaused = (
  episode: TreatmentEpisode,
  primaryDiagnosis: string,
): PatientStatusCardFormatLines => {
  const pausedDate = getActivePauseStartDate(episode?.pausedDetails);
  return {
    lineOne: pausedDate ? `Paused ${pausedDate}` : 'Paused',
    lineTwo: primaryDiagnosis || fallbackPrimaryDiagnosisLabel,
  };
};

const formatDischarged = (
  episode: TreatmentEpisode,
  primaryDiagnosis: string,
): PatientStatusCardFormatLines => {
  const primaryDiagnosisText =
    primaryDiagnosis || fallbackPrimaryDiagnosisLabel;
  let lineOne = 'Discharged';
  if (episode.closedDate) {
    const dischargedDateFormatted = DateTime.fromFormat(
      episode.closedDate,
      DateFormat.shortYearMonthDay,
    ).toFormat(DateFormat.mmDdYySlashes);
    lineOne = 'Discharged ' + dischargedDateFormatted;
  }
  return {
    lineOne,
    lineTwo: primaryDiagnosisText,
  };
};

const formatNeverStarted = (
  _: TreatmentEpisode,
  primaryDiagnosis: string,
): PatientStatusCardFormatLines => ({
  lineOne: 'Closed',
  lineTwo: primaryDiagnosis || fallbackPrimaryDiagnosisLabel,
});

const formatUnknown = (): PatientStatusCardFormatLines => ({
  lineOne: 'Unknown status',
  lineTwo: 'Unknown status',
});

const treatmentEpisodeStatusMapper: Record<
  TreatmentEpisodeStatus,
  (
    episode: TreatmentEpisode,
    primaryDiagnosis: string,
    tentativeDischargeDate?: string,
  ) => PatientStatusCardFormatLines
> = {
  [TreatmentEpisodeStatus.IN_TREATMENT]: formatInTreatment,
  [TreatmentEpisodeStatus.PAUSED]: formatPaused,
  [TreatmentEpisodeStatus.DISCHARGED]: formatDischarged,
  [TreatmentEpisodeStatus.NEVER_STARTED]: formatNeverStarted,
  [TreatmentEpisodeStatus.UNKNOWN]: formatUnknown,
  [TreatmentEpisodeStatus.VOID]: formatUnknown,
};

export const formatTreatmentEpisodeText = (
  patientStatus: string,
  episode: TreatmentEpisode,
  primaryDiagnosis: string,
  tentativeDischargeDate?: string,
): PatientStatusCardFormatLines => {
  const formatter =
    treatmentEpisodeStatusMapper[TreatmentEpisodeStatus[patientStatus]] ??
    treatmentEpisodeStatusMapper[TreatmentEpisodeStatus.UNKNOWN];
  return formatter(episode, primaryDiagnosis, tentativeDischargeDate);
};
